<template>
  <section class="theme">
    <TabsSwitchBar
      :model-value="section"
      :tabs="menuTabs"
      :role-class="setColorByRole"
      @tab-click="onTabClick"
      @update:model-value="section = $event"
    />

    <div class="section__task">
      <template v-if="section === 'day'">
        <DataSlider
          :items="daySliderItems"
          :model-value="selectedDaySliderItem"
          type="week"
          :border-bottom="true"
          @change="onDaySliderChange"
        />

        <EmptyStateNotice v-if="!isMealsLoading && !selectedDay?.menu?.length" :text="t('menuEmpty')" custom-class="mt-3" />
        <div v-else-if="!isMealsLoading" class="day-menu">
          <div class="nutritionalValue">
            <div class="text">
              <span>{{ t('EnergyNutritionalValue') }}</span>
              <span class="black">{{ selectedDay.nutritionalValue }} ккал</span>
            </div>
          </div>
          <div v-for="(item, index) in selectedDay.menu" :key="`dish-${index}`" class="border-line-bottom pb-2">
            <div class="menuTop">
              <span class="black">{{ item.name }}</span>
              <span class="black">{{ item.weight }}</span>
            </div>
            <div class="d-flex justify-content-between">
              <div class="d-flex flex-direction-column colorComposition">
                <span v-for="(ingredient, ingredientIndex) in item.ingredients || []" :key="`ing-${ingredientIndex}`">· {{ ingredient }}</span>
              </div>
              <div v-if="isParent" @click="selectMenuItem(item)">
                <span class="material-icons-outlined chat">comment</span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-if="section === 'week'">
        <DataSlider
          :items="weekSliderItems"
          :model-value="selectedWeekSliderItem"
          :month="weekSliderMonthMeta"
          type="month"
          @change="onWeekSliderChange"
        />

        <EmptyStateNotice v-if="!isMealsLoading && !weekSchedule.length" :text="t('menuEmpty')" />
        <div v-else-if="!isMealsLoading" class="week__body">
          <div v-for="item in weekSchedule" :key="item.key" class="weekDay" @click="goToDay(item.date)">
            <div class="menuTitle" :class="{ TodayBackground: isToday(item.date) }">
              <div>
                {{ formatDayNum(item.date) }}
                <span :class="{ TodayColor: isToday(item.date) }">· {{ formatWeekday(item.date) }}</span>
              </div>
              <span class="material-icons-outlined goToDay">arrow_forward_ios</span>
            </div>
            <div class="menuBody">
              <div class="border-line-bottom pb-2">
                <div class="d-flex justify-content-between" v-for="(dish, dishIndex) in item.menu" :key="`week-dish-${dishIndex}`">
                  <span>{{ dish.name }}</span>
                  <span>{{ dish.weight }}</span>
                </div>
              </div>
              <div class="menuNutritionalValue">
                <span>{{ t('EnergyNutritionalValue') }}</span>
                <span class="black ml-2">{{ item.nutritionalValue }} ккал</span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-if="section === 'chat' && isParent">
        <div v-if="selectedDish.name">
          <span class="selectedDish">{{ selectedDish.name }}</span>
          <div class="lineDote"></div>
        </div>
        <span>{{ t('reviewFeedback') }}</span>
        <textarea v-model="chat" rows="5" class="mb-2 mt-2 without-outline chatArea" placeholder="Текст сообщения..."></textarea>
        <button class="mt-4 submit f-13 btn-custom sendComment" @click="sendMsg">{{ t('review') }}</button>
        <button class="mt-4 f-13 btn-custom cancel" @click="clearChat">{{ t('cancel') }}</button>
      </template>

      <template v-if="section === 'chatMessage' && isParent">
        <div class="allMessages">
          <div v-for="(item, index) in chatMessages" :key="`msg-${index}`" class="chatMessages">
            <p v-if="item.record?.name" class="recordName">{{ item.record.name }}</p>
            <p>{{ item.message }}</p>
            <div v-if="item.answers?.length">
              <div class="chatAnswer chatMessages" v-for="(answer, answerIndex) in item.answers" :key="`ans-${answerIndex}`">
                <p class="fio">{{ answer.person_name }}</p>
                <p class="role">{{ answer.person_role }}</p>
                <p>{{ answer.message }}</p>
                <div>
                  <span class="material-icons-outlined time mr-2">watch_later</span>
                  <span class="chatTime">{{ formatChatTime(answer.ts) }}</span>
                </div>
              </div>
            </div>
            <div class="d-flex align-items-center justify-content-center flex-direction-column">
              <div class="d-flex align-items-center justify-content-center timeBg">
                <span class="material-icons-outlined time mr-2">watch_later</span>
                <span class="chatTime">{{ formatChatTime(item.ts) }}</span>
              </div>
              <div class="line"></div>
            </div>
          </div>

          <div v-if="canLoadMore" @click="loadMoreMessage">
            <span class="loadMore">{{ t('loadMore') }}</span>
          </div>
          <button class="mt-4 submit f-13 sendComment" @click="section = 'chat'">{{ t('feedback') }}</button>
        </div>
      </template>

      <template v-if="section === 'report'">
        <DataSlider
          :items="reportMonthItems"
          :model-value="selectedReportMonthItem"
          type="year"
          :border-bottom="true"
          @change="onReportMonthChange"
        />

        <div v-if="!isMarksLoading" class="week__card__body">
          <div class="month">
            <div class="week">
              <div class="day text-dark">{{ t('mon_short_text') }}</div>
              <div class="day text-dark">{{ t('tue_short_text') }}</div>
              <div class="day text-dark">{{ t('wed_short_text') }}</div>
              <div class="day text-dark">{{ t('thu_short_text') }}</div>
              <div class="day text-dark">{{ t('fri_short_text') }}</div>
              <div class="day text-dark text-pink">{{ t('sat_short_text') }}</div>
              <div class="day text-dark text-pink">{{ t('sun_short_text') }}</div>
            </div>
            <div class="week" v-for="(week, weekIndex) in reportWeeks" :key="`report-week-${weekIndex}`">
              <div class="day" v-for="day in week" :key="day.key">
                <span class="vam va circle work text-center" :class="day.class">
                  <span class="vam" :class="{ starDay: day.class.checked }">{{ day.day }}</span>
                  <i v-if="day.class.checked" class="fas fa-check text-green"></i>
                </span>
              </div>
            </div>
          </div>
          <div class="plans">
            <div class="holidays">
              <div class="holidaysTitle">
                <i class="fas fa-circle-info"></i>
                <span>{{ t('menuReport') }}</span>
              </div>
              <div class="holidaysInfo"><span>{{ t('menuText') }}</span></div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import TabsSwitchBar from '@/components/common/TabsSwitchBar.vue';
import EmptyStateNotice from '@/components/common/EmptyStateNotice.vue';
import {
  useMealMarksQuery,
  useMealsMenuQuery,
  useMenuChatMessagesQuery,
  usePeriodsQuery,
  useSendMenuChatMessageMutation
} from '@/queries';
import DataSlider from '@/components/common/DataSlider.vue';
import { useLoadingStore } from '@/stores/loading';
import { useTopbarStore } from '@/stores/topbar';
import { useViewerStore } from '@/stores/viewer';
import {
  buildPeriodSliderData,
  toPeriodDateKey,
  toUtcDaySeconds,
  toUtcDayTimestamp
} from '@/utils/periodSlider';

const { t } = useI18n();
const router = useRouter();
const topbarStore = useTopbarStore();
const loadingStore = useLoadingStore();
const viewerStore = useViewerStore();
viewerStore.initFromStorage();

const section = ref('day');
const chat = ref('');
const selectedDish = ref({});
const chatPage = ref(0);
const chatMessages = ref([]);
const canLoadMore = ref(false);
const selectedDayTs = ref(null);
const monthCursor = ref(startOfMonth(new Date()));
const isPeriodInitialized = ref(false);

const isParent = computed(() => viewerStore.role === 'parent');
const mealsPrivilegeType = computed(() => {
  const child = isParent.value ? viewerStore.selectedChild : viewerStore.profile;
  return child?.meals_privilege_type ?? child?._data?.meals_privilege_type ?? null;
});
const setColorByRole = computed(() => (viewerStore.role === 'child' ? 'active-pupil' : 'active-parent'));
const menuTabs = computed(() => [
  { value: 'day', label: t('day_button_label') },
  { value: 'week', label: t('month_button_label') },
  { value: 'chatMessage', label: t('chat'), visible: isParent.value },
  { value: 'report', label: t('report') }
]);
const periodsQuery = usePeriodsQuery({}, { staleTime: 5 * 60 * 1000 });

const periodSliderData = computed(() =>
  buildPeriodSliderData(periodsQuery.data.value || [], {
    locale: resolveLocale(),
    selectedTs: selectedDayTs.value
  })
);

const selectedDateTs = computed(() => toUtcDayTimestamp(selectedDayTs.value));
const { isPending: isMealsPending, data: mealsQuery } = useMealsMenuQuery(selectedDateTs, mealsPrivilegeType, {
  placeholderData: (previousData) => previousData,
  retry: false,
  enabled: computed(() => Number.isFinite(selectedDayTs.value)),
  refetchOnWindowFocus: false,
  refetchOnReconnect: false
});

const reportMonthTs = computed(() => toUtcDaySeconds(monthCursor.value));
const { isPending: isMarksPending, data: marksQuery } = useMealMarksQuery(reportMonthTs, {
  enabled: computed(() => section.value === 'report'),
  placeholderData: (previousData) => previousData
});

const chatQuery = useMenuChatMessagesQuery(chatPage, {
  enabled: computed(() => section.value === 'chatMessage' && isParent.value),
  placeholderData: (previousData) => previousData
});
const sendChatMutation = useSendMenuChatMessageMutation();

const isMealsLoading = computed(() => isMealsPending.value && !mealsQuery.value);
const isMarksLoading = computed(() => isMarksPending.value && !marksQuery.value);
const isPageLoading = computed(() => {
  if (section.value === 'report') return isMealsLoading.value || isMarksLoading.value;
  return isMealsLoading.value;
});
const selectedDateKey = computed(() => (Number.isFinite(selectedDayTs.value) ? toPeriodDateKey(selectedDayTs.value) : null));

const daySliderItems = computed(() => periodSliderData.value.days.items || []);
const selectedDaySliderItem = computed(
  () => daySliderItems.value.find((item) => toPeriodDateKey(item?.day?.date) === selectedDateKey.value) || null
);

const weekSliderItems = computed(() => periodSliderData.value.weeks.items || []);
const selectedWeekSliderItem = computed(() => {
  const ts = selectedDayTs.value;
  return (
    weekSliderItems.value.find((item) => ts >= item?.week?.start && ts < item?.week?.end) ||
    weekSliderItems.value[0] ||
    null
  );
});
const weekSliderMonthMeta = computed(() => periodSliderData.value.months.source || []);

const reportMonthItems = computed(() => periodSliderData.value.months.items || []);
const selectedReportMonthItem = computed(
  () =>
    reportMonthItems.value.find(
      (item) =>
        item.month.month === monthCursor.value.getMonth() && item.month.year === monthCursor.value.getFullYear()
    ) || null
);

const groupedMeals = computed(() => groupMealsByDate(mealsQuery.value || []));
const weekSchedule = computed(() => groupedMeals.value);
const selectedDay = computed(() => groupedMeals.value.find((item) => item.key === selectedDateKey.value) || null);


const markDateSet = computed(() => {
  const set = new Set();
  const rows = Array.isArray(marksQuery.value) ? marksQuery.value : [];
  rows.forEach((row) => {
    const ts = extractTs(row);
    if (!ts) return;
    set.add(toDateKey(new Date(ts)));
  });
  return set;
});

const reportWeeks = computed(() => {
  const monthStart = startOfMonth(monthCursor.value);
  const monthEnd = endOfMonth(monthCursor.value);
  const firstGrid = startOfWeekMonday(monthStart);
  const lastGrid = addDays(startOfWeekMonday(monthEnd), 6);

  const days = [];
  let cursor = new Date(firstGrid);
  while (cursor <= lastGrid) {
    const key = toDateKey(cursor);
    const inMonth = cursor.getMonth() === monthStart.getMonth();
    days.push({
      key,
      day: cursor.getDate(),
      class: {
        out: !inMonth,
        checked: markDateSet.value.has(key),
        rest: cursor.getDay() === 0
      }
    });
    cursor = addDays(cursor, 1);
  }

  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
});

watch(
  () => section.value,
  (value) => {
    if (value === 'chat' || value === 'chatMessage') {
      topbarStore.setTitle(t('feedback'));
    } else {
      topbarStore.setTitle(t('menu'));
    }
  },
  { immediate: true }
);

onMounted(() => {
  topbarStore.addButton({
    type: 'left',
    icon: 'keyboard_backspace',
    action: () => router.go(-1)
  });
});

watch(
  () => isPageLoading.value,
  (value) => {
    loadingStore.set('menu-page', value);
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  loadingStore.hide('menu-page');
});

watch(
  () => chatQuery.data.value,
  (rows) => {
    if (section.value !== 'chatMessage') return;
    const list = Array.isArray(rows) ? rows : [];
    canLoadMore.value = list.length >= 5;

    if (chatPage.value === 0) {
      chatMessages.value = list;
      return;
    }

    const seen = new Set(chatMessages.value.map((item, index) => `${item?.id || ''}-${item?.ts || ''}-${index}`));
    list.forEach((item, index) => {
      const key = `${item?.id || ''}-${item?.ts || ''}-${index}`;
      if (!seen.has(key)) chatMessages.value.push(item);
    });
  },
  { immediate: true }
);

watch(
  () => periodSliderData.value,
  (data) => {
    const items = data?.days?.items || [];
    if (!items.length) return;

    if (!isPeriodInitialized.value) {
      const initialDay = data.days.current || items[0];
      if (initialDay?.day?.date) {
        selectedDayTs.value = initialDay.day.date;
        monthCursor.value = startOfMonth(new Date(initialDay.day.date));
      }
      isPeriodInitialized.value = true;
      return;
    }

    const exists = items.some((item) => toPeriodDateKey(item?.day?.date) === selectedDateKey.value);
    if (!exists) {
      selectedDayTs.value = items[0].day.date;
    }
  },
  { immediate: true }
);

function selectDay(date) {
  const ts = new Date(date).getTime();
  if (!Number.isFinite(ts)) return;
  selectedDayTs.value = ts;
}

function onDaySliderChange(item) {
  const nextTs = item?.day?.date;
  if (!nextTs) return;
  selectedDayTs.value = Number(nextTs);
}

function onWeekSliderChange(item) {
  const nextTs = item?.week?.days?.[0]?.date || item?.week?.start;
  if (!nextTs) return;
  selectedDayTs.value = Number(nextTs);
}

function onReportMonthChange(item) {
  const monthValue = item?.month;
  if (!monthValue) return;
  const month = typeof monthValue === 'object' ? monthValue.month : monthValue;
  const year = typeof monthValue === 'object' ? monthValue.year : monthCursor.value.getFullYear();
  monthCursor.value = startOfMonth(new Date(year, month, 1));
}

function onTabClick(tab) {
  if (tab?.value === 'chatMessage') {
    openChatMessages();
  }
}

function goToDay(date) {
  selectDay(date);
  section.value = 'day';
}

function isToday(date) {
  return toPeriodDateKey(date) === toPeriodDateKey(Date.now());
}

function openChatMessages() {
  section.value = 'chatMessage';
  chatPage.value = 0;
  chatMessages.value = [];
}

function loadMoreMessage() {
  chatPage.value += 1;
}

function selectMenuItem(item) {
  selectedDish.value = item || {};
  section.value = 'chat';
}

async function sendMsg() {
  if (!chat.value?.trim()) return;
  const payload = {
    message: chat.value.trim(),
    table_name: 'meals_menu',
    ...(selectedDish.value?.id ? { record_id: selectedDish.value.id } : {})
  };
  await sendChatMutation.mutateAsync(payload);
  chat.value = '';
  selectedDish.value = {};
  openChatMessages();
}

function clearChat() {
  chat.value = '';
  selectedDish.value = {};
  section.value = 'day';
}

function resolveLocale() {
  return localStorage.getItem('translation') === 'kz' ? 'kk-KZ' : 'ru-RU';
}

function formatDayNum(date) {
  return new Date(date).toLocaleDateString(resolveLocale(), { day: 'numeric', month: 'long' });
}


function formatWeekday(date) {
  const value = new Date(date).toLocaleDateString(resolveLocale(), { weekday: 'long' });
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatChatTime(ts) {
  const date = ts ? new Date(Number(ts)) : new Date();
  return date.toLocaleDateString(resolveLocale(), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function toDateKey(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfWeekMonday(date) {
  const d = new Date(date);
  const day = d.getDay() || 7;
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - day + 1);
  return d;
}

function startOfMonth(date) {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfMonth(date) {
  const d = startOfMonth(date);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function extractTs(row) {
  if (typeof row === 'number') return row > 1e12 ? row : row * 1000;
  if (typeof row === 'string') {
    const parsed = Date.parse(row);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (!row || typeof row !== 'object') return null;

  const candidates = [row.ts, row.date, row._data, row.created_at, row.updated_at];
  for (const value of candidates) {
    if (typeof value === 'number') return value > 1e12 ? value : value * 1000;
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function groupMealsByDate(rows) {
  if (!Array.isArray(rows)) return [];
  const map = new Map();
  rows.forEach((item) => {
    const date = item?.date ? new Date(item.date) : null;
    if (!date || Number.isNaN(date.getTime())) return;
    const key = toDateKey(date);
    if (!map.has(key)) {
      map.set(key, {
        key,
        date,
        nutritionalValue: 0,
        menu: []
      });
    }
    const target = map.get(key);
    target.menu.push({
      id: item?.id,
      name: item?.name || '',
      weight: item?.weight || '',
      ingredients: Array.isArray(item?.ingredients) ? item.ingredients : []
    });
    target.nutritionalValue += Number(item?.calories || 0);
  });

  return [...map.values()].sort((a, b) => a.date - b.date);
}
</script>

<style scoped lang="scss">
.section__task {
  padding: 12px;
}

.date-slider {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.slider-btn {
  border: 1px solid #e0e0e0;
  background: #fff;
  width: 36px;
  height: 36px;
  border-radius: 4px;
}

.date-items {
  display: flex;
  overflow-x: auto;
  gap: 8px;
  flex: 1;
}

.date-item {
  border: 1px solid #e0e0e0;
  background: #fff;
  border-radius: 6px;
  padding: 8px 12px;
  min-width: 120px;
  text-align: left;
}

.date-item.active {
  border-color: #36b453;
  background: rgba(54, 180, 83, 0.12);
}

.subtitle {
  color: #828282;
  font-size: 12px;
}

.day-menu {
  padding: 10px 40px;
}

.nutritionalValue {
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding-bottom: 8px;
  margin-bottom: 15px;
  border-bottom: 1px dotted #828282;
}

.nutritionalValue .text {
  display: flex;
  flex-direction: column;
  color: rgba(0, 0, 0, 0.54);
  font-weight: 700;
}

.nutritionalValue .black {
  color: #000;
}

.menuTop {
  display: flex;
  justify-content: space-between;
  margin: 8px 0 4px;
}

.menuTop .black {
  color: #000;
  font-size: 16px;
  font-weight: 500;
}

.colorComposition {
  color: rgba(0, 0, 0, 0.54);
}

.chat {
  color: #2d9cdb;
  font-size: 16px;
  cursor: pointer;
}

.week__body {
  padding: 10px 40px;
  display: flex;
  justify-content: space-around;
  margin-left: 10px;
  flex-wrap: wrap;
}

.weekDay {
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  margin: 0 10px 16px;
  max-width: 390px;
  width: 100%;
}

.menuTitle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  font-size: 16px;
  height: 40px;
  padding: 7px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.goToDay {
  color: #bdbdbd;
  font-size: 14px;
}

.TodayBackground {
  background: rgba(54, 180, 83, 0.2);
}

.TodayColor {
  color: #36b453;
}

.menuBody {
  padding: 16px;
}

.menuNutritionalValue {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  color: rgba(0, 0, 0, 0.54);
}

.menuNutritionalValue .black {
  color: #000;
  font-weight: 500;
  font-size: 16px;
}

.chatArea {
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
}

.sendComment {
  background: #36b453;
  color: #fff;
  border: 1px solid #36b453;
  opacity: 1;
}

.sendComment:hover {
  opacity: 0.8;
}

.cancel {
  background: #e0e0e0;
  color: rgba(0, 0, 0, 0.8);
  border: 1px solid #e0e0e0;
  margin-left: 8px;
}

.allMessages {
  padding: 0 12px;
}

.chatMessages {
  min-height: 84px;
  margin-top: 12px;
}

.chatAnswer {
  margin: 12px -12px;
  padding: 12px;
  background: #f2f2f2;
}

.recordName {
  font-weight: 500;
  font-size: 14px;
  color: #2d9cdb;
  margin-bottom: 4px;
}

.fio {
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 4px;
}

.role {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
  margin-bottom: 16px;
}

.time {
  color: #36b453;
  font-size: 13px;
}

.chatTime {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
}

.timeBg {
  background: #fff;
  z-index: 90;
  padding: 0 10px;
}

.line {
  border-bottom: 1px dashed #828282;
  width: 100%;
  margin-top: -8px;
}

.lineDote {
  border-bottom: 1px dashed #828282;
  margin-top: 20px;
  margin-bottom: 12px;
}

.selectedDish {
  font-weight: 500;
  font-size: 20px;
  line-height: 32px;
  color: #2d9cdb;
}

.loadMore {
  margin-top: 12px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  text-decoration: underline;
  cursor: pointer;
}

.reportHeader {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 12px;
}

.reportMonth {
  min-width: 220px;
  text-align: center;
  font-weight: 500;
  text-transform: capitalize;
}

.week__card__body {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 24px;
  gap: 16px;
}

.month {
  width: 100%;
  border: 1px solid #e0e0e0;
}

.week {
  display: flex;
  text-align: center;
}

.day {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14.285%;
  min-height: 54px;
  border-right: 1px dashed #e0e0e0;
  border-bottom: 1px dashed #e0e0e0;
  text-transform: uppercase;
  background-color: #fff;
}

.day:last-child {
  border-right: none;
}

.circle.out {
  color: #cfcfcf;
}

.text-pink {
  color: #eb5757;
}

.plans {
  min-width: 366px;
  margin-left: 16px;
  border: 1px solid #e0e0e0;
  padding: 16px;
  overflow: auto;
}

.holidays {
  border: 1px solid #1890ff;
  border-radius: 8px;
  overflow: hidden;
}

.holidaysTitle {
  background: #1890ff;
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  padding: 10px 12px;
}

.holidaysInfo {
  background: #e6f7ff;
  font-size: 14px;
  line-height: 22px;
  color: #000;
  padding: 8px 12px;
}

@media screen and (min-width: 768px) {
  .section__task {
    margin-top: 50px;
  }
}

@media screen and (max-width: 767px) {
  .section__task {
    padding: 12px;
    margin-bottom: 20px;
  }

  .allMessages {
    margin-bottom: 64px;
    min-height: 100%;
  }

  .day-menu,
  .week__body {
    padding: 0 12px;
    margin-left: 0;
  }

  .week__card__body {
    flex-direction: column;
  }

  .plans {
    min-width: auto;
  }

  .sendComment {
    width: 100%;
  }
}

@media screen and (max-width: 767px) {
  .sendComment {
    position: fixed;
    bottom: 70px;
    left: 10px;
    right: 10px;
    width: 95%;
    z-index: 101;
  }
}
</style>
